import {
  Chip,
  ListItem,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import type { RankedMatchResponse } from "@bw/shared/types/generated";
import { Link as RouterLink } from "react-router-dom";
import { sidesFor } from "./matchSides";
import { matchDateLabel, matchSourceLabel } from "./competeDisplay";

type ResultKind = "won" | "lost" | "pending" | "disputed" | "invalid";
const RESULT_LABELS: Record<ResultKind, string> = {
  won: "Won",
  lost: "Lost",
  pending: "Pending",
  disputed: "Disputed",
  invalid: "Invalid",
};
const RESULT_COLORS: Record<ResultKind, "success" | "error" | "warning"> = {
  won: "success",
  lost: "error",
  pending: "warning",
  disputed: "warning",
  invalid: "warning",
};

function resultFor(
  status: string,
  winnerSide: number | null | undefined,
  perspectiveSide: 1 | 2 | null,
): ResultKind {
  if (status === "disputed") return "disputed";
  if (status === "invalid") return "invalid";
  if (perspectiveSide == null) return "pending";
  if (status === "completed")
    return winnerSide === perspectiveSide ? "won" : "lost";
  return "pending";
}

export interface MatchRowProps {
  match: RankedMatchResponse;
  perspectiveUserId: string | null;
  opponentName: string;
  facilityName: string;
  /** Detail is participant-only; perspective alone does not grant access. */
  canOpenDetail?: boolean;
}

export function MatchRow({
  match,
  perspectiveUserId,
  opponentName,
  facilityName,
  canOpenDetail = false,
}: MatchRowProps) {
  const sides = sidesFor(match.players, match.mode);
  const perspectiveSide: 1 | 2 | null =
    perspectiveUserId && sides.side1.includes(perspectiveUserId)
      ? 1
      : perspectiveUserId && sides.side2.includes(perspectiveUserId)
        ? 2
        : null;
  const result = resultFor(match.status, match.winner_side, perspectiveSide);
  const dateLabel = matchDateLabel(match.created_at);
  const modeLabel = match.mode === "doubles" ? "Doubles" : "Singles";
  const details = [
    matchSourceLabel(match.source),
    modeLabel,
    facilityName,
    dateLabel,
  ]
    .filter(Boolean)
    .join(" · ");

  const content = (
    <Stack
      direction="row"
      spacing={1}
      sx={{ width: "100%", alignItems: "center" }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography>
          <Typography
            component="span"
            sx={{
              position: "absolute",
              width: "1px",
              height: "1px",
              m: "-1px",
              p: 0,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            Opponent
          </Typography>
          vs {opponentName}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {details}
        </Typography>
      </Stack>
      <Chip
        label={RESULT_LABELS[result]}
        color={RESULT_COLORS[result]}
        variant="outlined"
        size="small"
        data-result={result}
      />
    </Stack>
  );

  return (
    <ListItem disablePadding={canOpenDetail}>
      {canOpenDetail ? (
        <ListItemButton
          component={RouterLink}
          to={`/me/performance/matches/${match.match_id}`}
        >
          {content}
        </ListItemButton>
      ) : (
        content
      )}
    </ListItem>
  );
}
